import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { companyInfo } from '../data';
import { imageUrl } from '../lib/images';

export default function Fjernvarme() {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en');
  const [openFaq, setOpenFaq] = useState(null);
  const [showBlueprintModal, setShowBlueprintModal] = useState(false);

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  const content = {
    da: {
      metaTitle: 'Fjernvarme Billund – Installation, udskiftning & service | Billund VVS',
      metaDesc:
        'Autoriseret fjernvarmeinstallatør i Billund og omegn. Ny fjernvarmeunit, udskiftning, tilslutning og service. Få et uforpligtende tilbud på effektiv varme.',
      canonical: 'https://billundvvs.netlify.app/varme/fjernvarme',
      heroH1: 'Fjernvarme',
      heroH2: 'Effektiv varme – installeret korrekt',
      heroLead:
        'Vi hjælper med installation, udskiftning og service af fjernvarmeunits. Vi sørger for en energieffektiv og driftssikker løsning, der passer til din bolig og dit behov.',
      features: [
        {
          icon: 'fa-solid fa-wrench',
          title: 'Ny fjernvarmeunit',
          desc: 'Vi installerer moderne og energieffektive fjernvarmeunits.',
        },
        {
          icon: 'fa-solid fa-arrows-rotate',
          title: 'Udskiftning',
          desc: 'Vi udskifter ældre anlæg til nye, driftssikre løsninger.',
        },
        {
          icon: 'fa-solid fa-gear',
          title: 'Tilslutning og indregulering',
          desc: 'Vi sikrer korrekt tilslutning og optimal indregulering.',
        },
        {
          icon: 'fa-solid fa-check',
          title: 'Service og vedligehold',
          desc: 'Vi tilbyder service, så din fjernvarmeinstallation fungerer optimalt.',
        },
      ],
      galleryTitle: 'Eksempler på fjernvarmeinstallationer',
      galleryItems: [
        {
          id: 'foer',
          image: '/_assets/fjernvarme-foer-aeldre-installation.jpg',
          alt: 'Ældre fjernvarmeinstallation før udskiftning',
          badge: 'FØR',
          badgeBg: 'bg-[#4b5563]',
          caption: 'Ældre installation',
        },
        {
          id: 'efter',
          image: '/_assets/fjernvarme-efter-ny-unit.jpg',
          alt: 'Ny moderne fjernvarmeunit efter montering',
          badge: 'EFTER',
          badgeBg: 'bg-[#d11218]',
          caption: 'Ny fjernvarmeunit med moderne styring',
        },
        {
          id: 'teknikrum',
          image: '/_assets/fjernvarme-teknikrum.jpg',
          alt: 'Fjernvarmeinstallation i teknikrum',
          caption: 'Fjernvarmeinstallation i teknikrum',
        },
        {
          id: 'kompakt',
          image: '/_assets/fjernvarme-kompakt-loesning.jpg',
          alt: 'Kompakt fjernvarmeløsning til mindre boliger',
          caption: 'Kompakt løsning til mindre boliger',
        },
      ],
      ctaButton: 'Få et uforpligtende tilbud',
      commercialTitle: 'Fjernvarme til både private og erhverv',
      commercialLead:
        'Vi rådgiver om den bedste løsning, der passer til din bolig, virksomhed eller ejendom. Vi hjælper med hele processen – fra rådgivning til færdig installation.',
      phoneCta: 'Ring 75 33 12 76',
      contactCta: 'Skriv til os',
      valuesTitle: 'Hvorfor vælge Billund Varme & Sanitet?',
      values: [
        {
          icon: 'fa-solid fa-certificate',
          title: 'Autoriseret VVS & Fjernvarme',
          desc: 'Vores faste montører er autoriserede og dækket af TEKNIQ Garantiordning.',
        },
        {
          icon: 'fa-solid fa-gauge-high',
          title: 'Optimal afkøling & returvarme',
          desc: 'Korrekt indregulering modvirker strafgebyr for dårlig afkøling fra varmeværket.',
        },
        {
          icon: 'fa-solid fa-toolbox',
          title: 'Anerkendte kvalitetsmærker',
          desc: 'Vi monterer driftssikre units fra METRO THERM, Danfoss, Redan og Termix.',
        },
        {
          icon: 'fa-solid fa-clock-rotate-left',
          title: 'Lokal erfaring siden 1963',
          desc: 'Over 60 års lokal erfaring med varmeforsyning i Billund, Grindsted og omegn.',
        },
      ],
      faqTitle: 'Ofte stillede spørgsmål om fjernvarme',
      faqs: [
        {
          q: 'Hvad koster udskiftning af en fjernvarmeunit?',
          a: 'Prisen på en ny fjernvarmeunit inklusiv autoriseret montering og bortskaffelse af det gamle anlæg ligger typisk mellem 28.000 og 45.000 kr. inkl. moms, afhængigt af boligens størrelse, varmtvandsbehov og eksisterende rørføring.',
        },
        {
          q: 'Hvorfor er god afkøling og lav returtemperatur vigtig?',
          a: 'Fjernvarmeværkerne stiller krav til, hvor godt din bolig udnytter varmen (afkølingen). Hvis returvandet er for varmt, kan varmeværket opkræve en ekstra afgift. Vi indregulerer dit anlæg nøjagtigt, så du udnytter energien optimalt og undgår tillægsgebyrer.',
        },
        {
          q: 'Hvor lang tid tager installationen af en ny fjernvarmeunit?',
          a: 'En standard udskiftning tager som regel 1 arbejdsdag. Vi lukker for varmen om morgenen og har dit nye anlæg tilsluttet og idriftsat samme eftermiddag, så du har varme og varmt vand igen inden aften.',
        },
        {
          q: 'Hvilke mærker fjernvarmeunits monterer I?',
          a: 'Vi monterer og servicerer alle anerkendte kvalitetsmærker på det danske marked, herunder METRO THERM, Danfoss, Redan og Gemina Termix.',
        },
      ],
    },
    en: {
      metaTitle: 'District Heating Billund – Installation, Replacement & Service | Billund VVS',
      metaDesc:
        'Authorized district heating installer in Billund and surrounding areas. New heating units, replacement, connection, and maintenance. Get a non-binding quote.',
      canonical: 'https://billundvvs.netlify.app/en/heating/district-heating',
      heroH1: 'District Heating',
      heroH2: 'Efficient heating – correctly installed',
      heroLead:
        'We help with installation, replacement, and servicing of district heating units. We provide energy-efficient, reliable solutions tailored to your home and specific requirements.',
      features: [
        {
          icon: 'fa-solid fa-wrench',
          title: 'New heating unit',
          desc: 'We install modern and highly energy-efficient district heating units.',
        },
        {
          icon: 'fa-solid fa-arrows-rotate',
          title: 'Replacement',
          desc: 'We replace older outdated systems with modern, reliable solutions.',
        },
        {
          icon: 'fa-solid fa-gear',
          title: 'Connection & balancing',
          desc: 'We ensure correct connection and precise hydraulic balancing.',
        },
        {
          icon: 'fa-solid fa-check',
          title: 'Service & maintenance',
          desc: 'We offer regular servicing so your heating system functions flawlessly.',
        },
      ],
      galleryTitle: 'Examples of district heating installations',
      galleryItems: [
        {
          id: 'foer',
          image: '/_assets/fjernvarme-foer-aeldre-installation.jpg',
          alt: 'Older heating installation before replacement',
          badge: 'BEFORE',
          badgeBg: 'bg-[#4b5563]',
          caption: 'Older installation',
        },
        {
          id: 'efter',
          image: '/_assets/fjernvarme-efter-ny-unit.jpg',
          alt: 'New modern district heating unit after installation',
          badge: 'AFTER',
          badgeBg: 'bg-[#d11218]',
          caption: 'New district heating unit with digital control',
        },
        {
          id: 'teknikrum',
          image: '/_assets/fjernvarme-teknikrum.jpg',
          alt: 'District heating installation in technical room',
          caption: 'Installation in technical room',
        },
        {
          id: 'kompakt',
          image: '/_assets/fjernvarme-kompakt-loesning.jpg',
          alt: 'Compact heating solution for smaller homes',
          caption: 'Compact solution for smaller homes',
        },
      ],
      ctaButton: 'Get a non-binding quote',
      commercialTitle: 'District heating for private and commercial clients',
      commercialLead:
        'We advise on the best solution tailored to your residential home, commercial property, or housing development. We support the entire process from consultation to final handover.',
      phoneCta: 'Call +45 75 33 12 76',
      contactCta: 'Contact us',
      valuesTitle: 'Why choose Billund Varme & Sanitet?',
      values: [
        {
          icon: 'fa-solid fa-certificate',
          title: 'Authorized plumbing & heating',
          desc: 'Our experienced plumbers are fully authorized and covered by TEKNIQ Guarantee.',
        },
        {
          icon: 'fa-solid fa-gauge-high',
          title: 'Optimal cooling & return flow',
          desc: 'Precise balancing avoids utility penalties for excessive return temperatures.',
        },
        {
          icon: 'fa-solid fa-toolbox',
          title: 'Premium Danish brands',
          desc: 'We install premium units from METRO THERM, Danfoss, Redan, and Termix.',
        },
        {
          icon: 'fa-solid fa-clock-rotate-left',
          title: 'Local experience since 1963',
          desc: 'Over 60 years of local expertise in Billund, Grindsted, and surrounding towns.',
        },
      ],
      faqTitle: 'Frequently asked questions about district heating',
      faqs: [
        {
          q: 'How much does a replacement district heating unit cost?',
          a: 'A new district heating unit including authorized installation and disposal of the old system usually ranges from DKK 28,000 to 45,000 including VAT, depending on house size, hot water demand, and pipework.',
        },
        {
          q: 'Why is return temperature cooling so crucial?',
          a: 'Utility providers enforce cooling benchmarks. Poor heat extraction leads to high return temperatures and utility penalty fees. Our precision balancing optimizes performance and keeps fees away.',
        },
        {
          q: 'How long does replacing a heating unit take?',
          a: 'A standard replacement typically takes one working day. Heating is disconnected in the morning, and the new unit is commissioned by afternoon.',
        },
        {
          q: 'Which brands of district heating units do you install?',
          a: 'We install and maintain leading market brands including METRO THERM, Danfoss, Redan, and Gemina Termix.',
        },
      ],
    },
  };

  const t = isEn ? content.en : content.da;

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDesc} />
        <link rel="canonical" href={t.canonical} />
        <meta property="og:title" content={t.metaTitle} />
        <meta property="og:description" content={t.metaDesc} />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/billund-fjernvarme-a-moderne-vvs-la-sninger-1.png"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white border-b border-gray-100 py-10 sm:py-16 lg:py-20 min-h-[460px] lg:min-h-[520px] flex items-center">
        {/* Right side cover image with smooth gradient flow */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-1/2 xl:w-[50%] 2xl:w-[52%] h-full pointer-events-none select-none overflow-hidden">
          <img
            src={imageUrl('/_assets/fjernvarme-hero-unit.jpg', 'width=1400,quality=90,format=auto')}
            alt={isEn ? 'Modern district heating installation' : 'Moderne fjernvarmeinstallation monteret i bryggers'}
            className="w-full h-full object-cover object-center"
            loading="eager"
            fetchPriority="high"
          />
          {/* Multi-stage horizontal gradient flow from white to transparent */}
          <div className="absolute inset-y-0 left-0 w-44 bg-gradient-to-r from-white via-white/80 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/20 to-transparent" />

          {/* Subtle certification pill in top/bottom area */}
          <div className="absolute bottom-5 right-6 pointer-events-auto bg-white/90 backdrop-blur-xs px-3.5 py-1.5 rounded-full shadow-xs border border-white/60 flex items-center gap-2 text-xs font-semibold text-[#09263f]">
            <i className="fa-solid fa-certificate text-[#d11218]" aria-hidden="true"></i>
            <span>TEKNIQ Autoriseret Installatør 2026–2028</span>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-xl lg:max-w-xl">
            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-bold text-[#09263f] tracking-tight leading-[1.1]">
              {t.heroH1}
            </h1>
            <h2 className="text-xl sm:text-2xl font-bold text-[#09263f] leading-snug mt-2">
              {t.heroH2}
            </h2>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed pt-3">
              {t.heroLead}
            </p>

            {/* Action Buttons */}
            <div className="pt-6 flex flex-wrap gap-4 items-center">
              <Link
                to={isEn ? '/en/get-a-quote' : '/tilbud'}
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#d11218] hover:bg-[#b50e14] text-white font-semibold text-base rounded-md shadow-sm transition-colors"
              >
                <span>{t.ctaButton}</span>
                <i className="fa-solid fa-arrow-right text-sm"></i>
              </Link>
              <a
                href={`tel:${companyInfo.phoneRaw}`}
                className="inline-flex items-center gap-2 px-5 py-3.5 border border-gray-300 hover:border-[#09263f] bg-white text-[#09263f] font-semibold text-base rounded-md transition-colors"
              >
                <i className="fa-solid fa-phone text-sm text-[#09263f]"></i>
                <span>{companyInfo.phone}</span>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="pt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-gray-500">
              <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                <i className="fa-solid fa-shield-halved text-emerald-600"></i>
                TEKNIQ Garantiordning
              </span>
              <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                <i className="fa-solid fa-award text-amber-600"></i>
                Autoriseret VVS & Fjernvarme
              </span>
            </div>

            {/* Mobile & Tablet fallback image with smooth flow */}
            <div className="mt-8 lg:hidden">
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-[8px] bg-gray-50 shadow-sm border border-gray-200">
                <img
                  src={imageUrl('/_assets/fjernvarme-hero-unit.jpg', 'width=1000,quality=85,format=auto')}
                  alt={isEn ? 'Modern district heating installation' : 'Moderne fjernvarmeinstallation monteret i bryggers'}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Strip / Fordelsbjælke (4 Columns with Navy Circle Badges) */}
      <section className="bg-white border-b border-gray-200 py-8 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {t.features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#09263f] text-white flex items-center justify-center shrink-0 shadow-xs">
                  <i className={`${feature.icon} text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#09263f] leading-snug">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-snug mt-1">
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Examples / Showcase Gallery (Eksempler på fjernvarmeinstallationer) */}
      <section className="bg-[#edf2f7] py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#09263f] mb-8 sm:mb-10">
            {t.galleryTitle}
          </h2>

          {/* 4 Columns Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
            {t.galleryItems.map((item) => (
              <div key={item.id} className="flex flex-col">
                {/* Image Container with high visual fidelity */}
                <div className="overflow-hidden rounded-lg shadow-sm border border-gray-200/80 bg-white aspect-[3/4] sm:aspect-[4/5] group">
                  <img
                    src={imageUrl(item.image, 'width=700,quality=85,format=auto')}
                    alt={item.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>

                {/* Badge if present (FØR / EFTER) */}
                {item.badge && (
                  <div
                    className={`w-full ${item.badgeBg} text-white text-center font-bold uppercase tracking-wider py-2 rounded-md text-sm mt-3 shadow-xs`}
                  >
                    {item.badge}
                  </div>
                )}

                {/* Caption Text */}
                <p
                  className={`text-center font-medium text-gray-800 text-sm sm:text-base leading-snug ${
                    item.badge ? 'mt-2' : 'mt-3'
                  }`}
                >
                  {item.caption}
                </p>
              </div>
            ))}
          </div>

          {/* Centered Primary CTA Button */}
          <div className="mt-10 sm:mt-12 text-center">
            <Link
              to={isEn ? '/en/get-a-quote' : '/tilbud'}
              className="inline-flex items-center justify-center gap-2.5 px-8 py-3.5 bg-[#d11218] hover:bg-[#b50e14] text-white font-semibold text-base sm:text-lg rounded-md shadow-sm transition-all transform hover:-translate-y-0.5"
            >
              <span>{t.ctaButton}</span>
              <i className="fa-solid fa-arrow-right text-sm"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom Section: Commercial & Private (Fjernvarme til både private og erhverv) */}
      <section className="bg-white py-12 sm:py-16 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7">
              <div className="w-14 h-14 rounded-full bg-[#09263f] text-white flex items-center justify-center shrink-0 mb-4 shadow-xs">
                <i className="fa-solid fa-house text-2xl"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#09263f] mb-3">
                {t.commercialTitle}
              </h2>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed max-w-2xl">
                {t.commercialLead}
              </p>

              {/* Direct Contact Buttons */}
              <div className="mt-6 flex flex-wrap gap-4 items-center">
                <a
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#09263f] hover:bg-[#071d31] text-white font-semibold text-base rounded-md transition-colors shadow-xs"
                >
                  <i className="fa-solid fa-phone text-sm"></i>
                  <span>{t.phoneCta}</span>
                </a>
                <Link
                  to={isEn ? '/en/contact' : '/kontakt'}
                  className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100 text-gray-800 font-semibold text-base rounded-md transition-colors"
                >
                  <i className="fa-regular fa-envelope text-sm"></i>
                  <span>{t.contactCta}</span>
                </Link>
                <button
                  type="button"
                  onClick={() => setShowBlueprintModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-900 border border-dashed border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  title="Vis komplet referenceoversigt"
                >
                  <i className="fa-solid fa-expand text-xs"></i>
                  <span>{isEn ? 'View blueprint preview' : 'Se oversigtsgrafik'}</span>
                </button>
              </div>
            </div>

            {/* Right Image: Macro close up of copper pipes & valve */}
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-xl shadow-md border border-gray-200 aspect-[16/11] sm:aspect-[4/3] bg-gray-50">
                <img
                  src={imageUrl('/_assets/fjernvarme-roer-ventil-detail.jpg', 'width=800,quality=85,format=auto')}
                  alt={isEn ? 'Close-up of district heating copper pipes and valves' : 'Nærbillede af kobberrør og fjernvarmeventil'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values & Trust Section */}
      <section className="bg-slate-50 py-12 sm:py-16 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#09263f]">
              {t.valuesTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.values.map((v, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-6 border border-gray-200/80 shadow-xs hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-red-50 text-[#d11218] flex items-center justify-center mb-4">
                  <i className={`${v.icon} text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#09263f] mb-2">{v.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="bg-white py-12 sm:py-16 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#09263f] mb-8 text-center">
            {t.faqTitle}
          </h2>
          <div className="space-y-4">
            {t.faqs.map((faq, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-xs"
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(idx)}
                  className="w-full px-6 py-4 text-left font-bold text-[#09263f] hover:bg-gray-50 flex items-center justify-between transition-colors cursor-pointer"
                >
                  <span className="text-base sm:text-lg pr-4">{faq.q}</span>
                  <i
                    className={`fa-solid fa-chevron-down text-gray-400 text-sm transition-transform duration-200 shrink-0 ${
                      openFaq === idx ? 'rotate-180 text-[#d11218]' : ''
                    }`}
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5 pt-1 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 bg-gray-50/50">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal with exact reference design layout */}
      {showBlueprintModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 relative">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-4">
              <h3 className="text-xl font-bold text-[#09263f]">
                {isEn ? 'District Heating Design Overview' : 'Komplet referenceoversigt for Fjernvarme'}
              </h3>
              <button
                type="button"
                onClick={() => setShowBlueprintModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-colors"
                aria-label="Luk"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {isEn
                  ? 'Official reference layout for Billund Varme & Sanitet A/S district heating services.'
                  : 'Gennemført referenceopstilling for Billund Varme & Sanitet A/S fjernvarmeunit og installationer.'}
              </p>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-100">
                <img
                  src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/billund-fjernvarme-a-moderne-vvs-la-sninger-1.png"
                  alt="Billund Fjernvarme referenceopstilling"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setShowBlueprintModal(false)}
                className="px-5 py-2.5 bg-[#09263f] hover:bg-[#071d31] text-white font-medium text-sm rounded-md transition-colors"
              >
                {isEn ? 'Close' : 'Luk'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
